import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../services/api.service';
import { GcsFileItem } from '../../models/rag.model';

@Component({
  selector: 'app-rag-documents',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './rag-documents.component.html',
  styleUrls: ['./rag-documents.component.scss']
})
export class RagDocumentsComponent implements OnInit {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('downloadAnchor') downloadAnchor!: ElementRef<HTMLAnchorElement>;

  currentPath: string = '';
  pathParts: string[] = [];
  files: GcsFileItem[] = [];
  isLoading: boolean = false;
  error: string | null = null;
  successMessage: string | null = null;

  // New directory modal
  showNewDirModal: boolean = false;
  newDirName: string = '';

  // Upload state
  isUploading: boolean = false;
  uploadProgress: string = '';

  // Download state
  downloadingFile: string | null = null;

  useCode: string = '';

  constructor(private apiService: ApiService) {}

  ngOnInit(): void {
    this.loadFiles();
  }

  loadFiles(): void {
    this.isLoading = true;
    this.error = null;

    this.apiService.gcsListFiles(this.currentPath, this.useCode || undefined).subscribe({
      next: (files) => {
        this.files = files.sort((a, b) => {
          if (a.isDirectory !== b.isDirectory) return a.isDirectory ? -1 : 1;
          return a.name.localeCompare(b.name);
        });
        this.isLoading = false;
        this.updatePathParts();
      },
      error: (err) => {
        this.error = 'Failed to load files: ' + (err.error?.message || err.message);
        this.isLoading = false;
      }
    });
  }

  updatePathParts(): void {
    this.pathParts = this.currentPath ? this.currentPath.split('/').filter(p => p) : [];
  }

  navigateTo(path: string): void {
    this.currentPath = path;
    this.loadFiles();
  }

  navigateToIndex(index: number): void {
    if (index < 0) {
      this.currentPath = '';
    } else {
      this.currentPath = this.pathParts.slice(0, index + 1).join('/');
    }
    this.loadFiles();
  }

  openItem(item: GcsFileItem): void {
    if (item.isDirectory) {
      this.currentPath = item.path;
      this.loadFiles();
    }
  }

  goUp(): void {
    if (this.pathParts.length > 0) {
      this.pathParts.pop();
      this.currentPath = this.pathParts.join('/');
      this.loadFiles();
    }
  }

  openNewDirModal(): void {
    this.newDirName = '';
    this.showNewDirModal = true;
  }

  closeNewDirModal(): void {
    this.showNewDirModal = false;
  }

  createDirectory(): void {
    if (!this.newDirName.trim()) return;

    const dirPath = this.currentPath
      ? `${this.currentPath}/${this.newDirName}`
      : this.newDirName;

    this.apiService.gcsCreateDirectory(dirPath, this.useCode || undefined).subscribe({
      next: () => {
        this.successMessage = 'Directory created successfully';
        this.closeNewDirModal();
        this.loadFiles();
        setTimeout(() => this.successMessage = null, 3000);
      },
      error: (err) => {
        this.error = 'Failed to create directory: ' + (err.error?.error || err.message);
      }
    });
  }

  triggerFileUpload(): void {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    this.uploadFile(file);
    input.value = '';
  }

  uploadFile(file: File): void {
    const uploadPath = this.currentPath
      ? `${this.currentPath}/${file.name}`
      : file.name;

    this.isUploading = true;
    this.uploadProgress = `Uploading ${file.name}...`;

    this.apiService.gcsUploadFile(file, uploadPath, this.useCode || undefined).subscribe({
      next: (response) => {
        if (response.success) {
          this.successMessage = 'File uploaded successfully';
          this.loadFiles();
        } else {
          this.error = response.error || 'Upload failed';
        }
        this.isUploading = false;
        this.uploadProgress = '';
        setTimeout(() => this.successMessage = null, 3000);
      },
      error: (err) => {
        this.error = 'Upload failed: ' + (err.error?.error || err.message);
        this.isUploading = false;
        this.uploadProgress = '';
      }
    });
  }

  formatSize(bytes?: number): string {
    if (!bytes) return '-';
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  }

  downloadFile(item: GcsFileItem): void {
    this.downloadingFile = item.path;
    this.error = null;

    this.apiService.gcsDownloadFile(item.path, this.useCode || undefined).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const safeName = item.name.replace(/[^a-zA-Z0-9._\-\s]/g, '_');
        const anchor = this.downloadAnchor.nativeElement;
        anchor.href = url;
        anchor.download = safeName;
        anchor.click();
        setTimeout(() => window.URL.revokeObjectURL(url), 0);
        this.downloadingFile = null;
      },
      error: (err) => {
        this.error = 'Failed to download file: ' + (err.error?.message || err.message);
        this.downloadingFile = null;
      }
    });
  }
}

